// Dữ liệu khung dùng chung cho mọi giao diện thành viên (Gốc và Glass): menu theo
// cấp, thanh tab đáy, icon/tông từng mục, mục đang mở, và hook gom trạng thái tài
// khoản. Tách nguyên văn từ DashboardLayout — không đổi hành vi.

import { createContext, useContext, useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  LayoutDashboard, Network, Store, Boxes, PackageOpen, Coins,
  Tag, ClipboardList, Users, BarChart3, SlidersHorizontal, ShieldCheck, HandCoins,
  House, Truck, ArrowLeftRight, MapPin, FileText, Landmark, Lock, Bell, MessageSquareText, Inbox,
  MessagesSquare,
  type LucideIcon,
} from 'lucide-react'
import type { Tone } from '@/components/ui/kpi-tile'
import { getAuthState, subscribe, logout, selectShop, type Shop } from '@/lib/auth'
import { get } from '@/lib/api'
import { useAuthState } from '@/hooks/useAuthState'
import type { BrandInfo } from '@/lib/brand'
import { hasAccess, getAccountLevelInfo, getRoleTheme, getHomeRouteForTenant, type RoleTheme } from '@/lib/permissions'
import {
  WORK_MODE_LABEL,
  hasWorkModes,
  homeForMode,
  markModeSwitch,
  modeForPath,
  setWorkMode,
  useWorkMode,
  useWorkModeChosen,
  type WorkMode,
} from '@/lib/workMode'
import { toast } from 'sonner'
import { NPP_BOTTOM_LABEL, NPP_BOTTOM_PATHS, NPP_NAV } from '@/npp/nav'
import { canWith, useNppAdmin } from '@/lib/nppAdmin'
import { canHandleDownline } from '@shoptrack/common/tickets'
import { useGomdonAccess } from '@/features/gomdon/access'
import { withoutGomdon } from '@/features/gomdon/paths'
import { useGomdonNavBadge } from '@/features/gomdon/unread'
import { useViewportMode } from '@/hooks/useViewportMode'
import { askLogoutSurvey } from '@/components/survey/logout-survey'
import {
  PHONE_SETTINGS, accountMenuItems, activeDockTab, activePhoneTab, navMatchPath, phoneDockItems, phoneMoreItems, phoneSurface, phoneTabs,
  type PhoneIcon, type PhoneLink, type PhoneMoreItem,
} from './phone-nav'

/** Chữ trong vòng avatar: chữ cái đầu của từ đầu tiên bắt đầu bằng chữ. */
export function initialOf(name: string): string {
  const word = name.trim().split(/\s+/).find((w) => /^\p{L}/u.test(w))
  return (word?.[0] ?? 'S').toUpperCase()
}

export function cleanShopName(name?: string | null): string {
  if (!name) return ''
  return name.replace(/\s*\(Level\s*\d+\)/gi, '').trim()
}

// ── Thông tin đơn vị (GET /settings/shop) ─────────────────────────
/** Kết quả GET /settings/shop. `npp` = nhà phân phối của đơn vị (docs/KE_HOACH_NHIEU_NPP.md §3.16). */
export interface ShopSettings {
  shop: any
  senderName: string | null
  displayName: string
  npp?: NppBrief
  /** Thương hiệu NPP áp cho cả cây (docs/KE_HOACH_THUONG_HIEU_NPP.md) — components/brand/BrandSync.tsx áp */
  brand?: BrandInfo
  /** Ảnh đại diện của đơn vị (components/settings/ShopAvatar.tsx) — chưa có / bản api cũ → chữ cái đầu tên */
  avatarUrl?: string | null
}

/** NPP của đơn vị. `isDefault` = Sunrise (NPP mặc định). */
export interface NppBrief {
  id: string
  displayName: string
  isDefault: boolean
}

/** Tên công ty cấp trên khi chưa tải xong — đúng tên của NPP mặc định (Sunrise). */
export const DEFAULT_NPP_NAME = 'NPP'
const DEFAULT_NPP: NppBrief = { id: 'sunrise', displayName: DEFAULT_NPP_NAME, isDefault: true }

/** Thông tin đơn vị đang mở — cùng khoá cache ['shop-settings', shopId] với các trang khác. */
export function useShopSettings(shopId: string | null) {
  return useQuery({
    queryKey: ['shop-settings', shopId],
    queryFn: () => get<ShopSettings>('/settings/shop').catch(() => null),
    enabled: !!shopId,
    staleTime: 30_000,
  })
}

/**
 * Tên NPP của đơn vị — dùng ở mọi chữ nói về công ty cấp trên (trả hoa hồng, nhận tiền ship,
 * đối soát, liên hệ…). Sunrise ra "ShopTrack" như cũ; chưa tải xong cũng ra "ShopTrack".
 * Chữ "ShopTrack" là tên sản phẩm (logo, tài khoản ShopTrack, Điều khoản) thì giữ nguyên.
 */
export function useNppName(): string {
  return useNpp().displayName
}

/** NPP của đơn vị đang mở; chưa tải xong → coi như NPP mặc định (giữ chữ như trước khi có nhiều NPP). */
export function useNpp(): NppBrief {
  const { activeShopId } = useAuthState()
  const { data } = useShopSettings(activeShopId)
  return data?.npp?.displayName ? data.npp : DEFAULT_NPP
}

// ── Điều hướng ────────────────────────────────────────────────────
// Nhãn ngắn hết mức còn phân biệt được. Sidebar rộng 224px: nhãn cũ kiểu
// "Cây mạng lưới Đại lý & CTV" hay "Bảng Kê & Chi Trả (Vòng 2)" bị cắt cụt ở
// CẢ BỐN cấp, nên nửa sau của nhãn chưa bao giờ đọc được.
export interface NavItem {
  to: string
  label: string
  group?: string
  /** Mục con — thụt vào, không chiếm một vị trí cấp 1 trong đầu người dùng. */
  child?: boolean
}

// Đại lý / CTV có hai chế độ (docs/KE_HOACH_CHIA_GIAO_DIEN_QUAN_LY_BAN_HANG.md):
// "Quản lý" — mạng lưới; "Bán hàng" — giống hệt Shop. Mỗi chế độ ≤ 7 mục + Cài đặt.
// Quản lý không có mục Đơn hàng (31/10/2026): đơn của cấp dưới mở từ sơ đồ ("Xem đơn"), trang đó sáng mục Hệ thống.
export const AGENCY_MANAGE_NAV: NavItem[] = [
  { to: '/agency/overview', label: 'Tổng quan' },
  { to: '/network', label: 'Hệ thống' },
  { to: '/rates', label: 'Bảng giá' },
  { to: '/commissions', label: 'Chiết khấu' },
  { to: '/reconcile', label: 'Đối soát' },
  { to: '/reports', label: 'Báo cáo' },
  { to: '/settings', label: 'Cài đặt' },
]

export const CTV_MANAGE_NAV: NavItem[] = [
  { to: '/ctv/dashboard', label: 'Tổng quan' },
  { to: '/network', label: 'Hệ thống' },
  { to: '/rates', label: 'Bảng giá' },
  { to: '/commissions/me', label: 'Chiết khấu' },
  { to: '/reconcile', label: 'Đối soát' },
  { to: '/reports', label: 'Báo cáo' },
  { to: '/settings', label: 'Cài đặt' },
]

/**
 * Menu của Shop — cũng là chế độ Bán hàng của Đại lý / CTV (giống hệt nhau).
 * "Gom Đơn" (một trang /inbox: tin nhắn + bình luận + kết nối kênh) chỉ hiện khi engine Gom Đơn mở cho đơn vị đang mở
 * (useGomdonAccess); không thì bị lọc bỏ trước mọi phép tính menu → menu y như khi chưa có Gom Đơn.
 */
export const STORE_NAV: NavItem[] = [
  { to: '/app', label: 'Tổng quan' },
  { to: '/orders', label: 'Đơn hàng' },
  { to: '/inbox', label: 'Gom Đơn' },
  { to: '/products', label: 'Sản phẩm' },
  { to: '/customers', label: 'Khách hàng' },
  { to: '/rates', label: 'Bảng giá' },
  { to: '/reconcile', label: 'Đối soát' },
  { to: '/reports', label: 'Báo cáo' },
  { to: '/settings', label: 'Cài đặt' },
]

/** STORE_NAV bỏ các mục Gom Đơn khi đơn vị chưa dùng được Gom Đơn. */
export function storeNavFor(gomdonEnabled: boolean): NavItem[] {
  return gomdonEnabled ? STORE_NAV : withoutGomdon(STORE_NAV)
}

export const ADMIN_NAV: NavItem[] = [
  { to: '/agency/overview', label: 'Tổng quan', group: 'Hệ thống' },
  { to: '/network', label: 'Mạng lưới', group: 'Hệ thống' },
  { to: '/agency/orders', label: 'Đơn hàng', group: 'Hệ thống' },

  { to: '/reconcile', label: 'Đối soát', group: 'Dòng tiền' },

  { to: '/settings', label: 'Cài đặt' },
  { to: '/admin', label: 'Quản trị' },
]

// ── Khung điện thoại (tab đáy, quạt Thêm, menu tài khoản) ─────────
// Dữ liệu ở phone-nav.ts (thuần, có test) — chép theo app iOS. Ở đây chỉ gắn icon lucide.
const PHONE_ICON: Record<PhoneIcon, LucideIcon> = {
  home: House,
  dashboard: LayoutDashboard,
  orders: ClipboardList,
  products: Boxes,
  shipping: Truck,
  network: Network,
  store: Store,
  shopOrders: PackageOpen,
  money: Coins,
  customers: Users,
  reports: BarChart3,
  rates: Tag,
  commission: HandCoins,
  switch: ArrowLeftRight,
  settings: SlidersHorizontal,
  admin: ShieldCheck,
  pickup: MapPin,
  tariff: FileText,
  bank: Landmark,
  security: Lock,
  notifications: Bell,
  feedback: MessageSquareText,
  costTable: FileText,
  downline: Inbox,
}

export type PhoneTab = PhoneLink & { Icon: LucideIcon }
export type PhoneMoreEntry = PhoneMoreItem & { Icon: LucideIcon }

const withIcon = <T extends { icon: PhoneIcon }>(x: T): T & { Icon: LucideIcon } => ({ ...x, Icon: PHONE_ICON[x.icon] })

// Khu Quản trị NPP trên điện thoại: 4 ô hay dùng của menu NPP (NPP_BOTTOM_PATHS); "Thêm" mở cả menu NPP (GlassLayout /
// DashboardLayout) vì quá nhiều mục cho quạt. Khoá icon chỉ để đủ kiểu PhoneLink — ô vẽ bằng icon của menu NPP.
const NPP_PHONE_ICON: Record<string, PhoneIcon> = { '/npp': 'dashboard', '/npp/network': 'network', '/npp/orders': 'orders', '/npp/payouts': 'money' }
const NPP_ACCOUNT_MENU: PhoneTab[] = [withIcon({ to: '/npp/account', label: 'Tài khoản', icon: 'security' as const })]

/** Ô sáng ở khu NPP: "/npp" chỉ khi đúng trang Tổng quan (mọi trang NPP đều bắt đầu bằng /npp); không ô nào → Thêm. */
function nppActiveTab(pathname: string, tabs: PhoneLink[]): string | 'more' {
  const hit = tabs.find((t) => (t.to === '/npp' ? pathname === '/npp' : pathname === t.to || pathname.startsWith(t.to + '/')))
  return hit?.to ?? 'more'
}

// ── Icon + tông màu cho từng mục menu (theo đường dẫn, dùng chung mọi cấp) ──
export const NAV_META: Record<string, { icon: LucideIcon; tone: Tone }> = {
  '/agency/overview': { icon: LayoutDashboard, tone: 'blue' },
  '/ctv/dashboard': { icon: LayoutDashboard, tone: 'blue' },
  '/app': { icon: LayoutDashboard, tone: 'blue' },
  '/network': { icon: Network, tone: 'blue' },
  '/agency/orders': { icon: PackageOpen, tone: 'blue' },
  '/ctv/orders': { icon: PackageOpen, tone: 'green' },
  '/rates': { icon: Tag, tone: 'violet' },
  '/commissions': { icon: HandCoins, tone: 'violet' },
  '/commissions/me': { icon: HandCoins, tone: 'orange' },
  '/orders': { icon: ClipboardList, tone: 'green' },
  '/inbox': { icon: MessagesSquare, tone: 'blue' },
  '/products': { icon: Boxes, tone: 'green' },
  '/customers': { icon: Users, tone: 'green' },
  '/reports': { icon: BarChart3, tone: 'green' },
  '/reconcile': { icon: Coins, tone: 'orange' },
  '/settings': { icon: SlidersHorizontal, tone: 'gray' },
  '/admin': { icon: ShieldCheck, tone: 'gray' },
  // Khu Quản trị NPP
  ...Object.fromEntries(NPP_NAV.map((i) => [i.to, { icon: i.icon, tone: i.tone }])),
}

// Gom Đơn trên điện thoại giao diện Glass: một ô "Gom Đơn" trong quạt "Thêm" mở trang Gom Đơn (/inbox).
const GOMDON_FAN_ITEM: PhoneMoreEntry = { kind: 'link', to: '/inbox', label: 'Gom Đơn', icon: 'feedback', Icon: MessagesSquare }

/**
 * Mục đang mở = mục có `to` DÀI NHẤT khớp đường dẫn hiện tại.
 *
 * Kiểu `pathname.startsWith(to)` cũ làm "Đối soát" và "Đối soát BEST" cùng
 * sáng một lúc, vì `/x/reconcile/carrier` bắt đầu bằng `/x/reconcile`.
 */
export function activeNavPath(pathname: string, items: { to: string }[]): string | null {
  let best: string | null = null
  for (const it of items) {
    // "/npp" là Tổng quan của khu Quản trị NPP — mọi trang khác cũng nằm dưới /npp nên chỉ khớp đúng tuyệt đối
    const exactOnly = it.to === '/npp'
    if (pathname === it.to || (!exactOnly && pathname.startsWith(it.to + '/'))) {
      if (!best || it.to.length > best.length) best = it.to
    }
  }
  return best
}

/**
 * Trang cần cả khung nhìn — sơ đồ mạng lưới cây ngang (chốt 29/10): trên MÁY TÍNH khung ẩn menu trái (nút ☰ ở thanh
 * trên mở menu đè lên), nội dung hết bề ngang và cao đúng một màn để cây tự cuộn bên trong. Điện thoại không đổi.
 */
export const WIDE_SHELL_PATHS: readonly string[] = ['/npp/network', '/network']

export function isWideShellPath(pathname: string): boolean {
  return WIDE_SHELL_PATHS.includes(pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname)
}

/** Đang ở khu Quản trị NPP (/npp…) — khung đổi menu, màu và bỏ các phần chỉ đơn vị mới có. */
export function isNppPath(pathname: string): boolean {
  return pathname === '/npp' || pathname.startsWith('/npp/')
}

/** Trạng thái tài khoản + menu của khung — cùng một nguồn cho mọi giao diện. */
export function useShellModel() {
  const nav = useNavigate()
  const location = useLocation()
  const [auth, setAuth] = useState(getAuthState())
  const nppAdmin = useNppAdmin()
  const nppMode = isNppPath(location.pathname)
  const viewport = useViewportMode()

  useEffect(() => {
    const unsub = subscribe((next) => setAuth(next))
    return () => {
      unsub()
    }
  }, [])

  const shop = auth.shops.find((s) => s.shopId === auth.activeShopId)
  const role = shop?.role
  const tenantType = shop?.tenantType || (role === 'agency' || role === 'ctv' ? role : 'shop')
  const roleTheme = getRoleTheme(tenantType, role)
  // Nhân viên của đơn vị (docs/KE_HOACH_THANH_VIEN_DON_VI.md): menu / nút theo quyền chủ cấp; chủ đơn vị: staff = null
  const staff = role === 'staff' ? shop?.staff ?? { roleId: null, roleName: null, permissions: [] } : null
  const isStaff = staff !== null
  const baseLevel = getAccountLevelInfo(tenantType, role)
  const levelInfo = isStaff ? { ...baseLevel, levelTag: staff.roleName ? `Nhân viên · ${staff.roleName}` : 'Nhân viên' } : baseLevel
  // Giao diện cấp trên bật cho đơn vị (docs/KE_HOACH_CHON_GIAO_DIEN_CAP_DUOI.md) — trang của giao diện tắt không vào được
  const workModes = shop?.workModes
  const canAccess = (to: string) => hasAccess(role, to, tenantType, staff, workModes)

  const { data: shopSettings } = useShopSettings(auth.activeShopId)
  const shopName = cleanShopName(shopSettings?.displayName || shop?.name || '')
  const nppName = shopSettings?.npp?.displayName || DEFAULT_NPP_NAME

  // Gom Đơn: hỏi engine (lỗi / chưa mở → false, im lặng). Mục Gom Đơn chỉ vào menu bán hàng khi `enabled`; phép tính
  // chế độ Quản lý / Bán hàng bên dưới dùng menu KHÔNG có Gom Đơn — engine trả lời muộn hay tắt cũng không đổi chế độ.
  const gomdon = useGomdonAccess()
  const baseStoreNav = storeNavFor(false)
  const storeNav = storeNavFor(gomdon.enabled)
  // Số hội thoại chưa đọc (tin nhắn + bình luận) trên mục "Gom Đơn" (chỉ hỏi engine khi `enabled`)
  const navBadgeFor = useGomdonNavBadge()

  // Chế độ Quản lý / Bán hàng (chỉ Đại lý, CTV): đường dẫn quyết định khi trang chỉ thuộc một
  // bên; trang dùng chung giữ chế độ đang dùng (nhớ theo trình duyệt).
  // Chế độ nào không vào được trang RIÊNG nào của nó (Bảng giá, Đối soát, Báo cáo, Cài đặt có ở cả hai) thì không hiện
  // công tắc, dùng chế độ còn lại — nhân viên thiếu quyền, hoặc cấp trên chỉ bật một giao diện cho đơn vị.
  const manageNav = tenantType === 'agency' ? AGENCY_MANAGE_NAV : CTV_MANAGE_NAV
  const usable = (items: NavItem[]) => items.filter((item) => canAccess(item.to))
  const hasPages = (items: NavItem[], m: WorkMode) => usable(items).some((item) => modeForPath(item.to) === m)
  const manageOk = hasWorkModes(tenantType) && hasPages(manageNav, 'manage')
  const sellOk = hasPages(baseStoreNav, 'sell')
  const manageOnly = !nppMode && manageOk && !sellOk
  const canSwitchMode = !nppMode && manageOk && sellOk
  const storedMode = useWorkMode()
  const modeChosen = useWorkModeChosen()
  const pathMode = modeForPath(location.pathname)
  const mode: WorkMode = canSwitchMode ? pathMode ?? storedMode : manageOnly ? 'manage' : 'sell'
  useEffect(() => {
    if (canSwitchMode && pathMode && pathMode !== storedMode) setWorkMode(pathMode)
    // Chỉ một giao diện: chế độ nhớ theo giao diện đó — trang dùng chung (Báo cáo, Bảng giá) đọc chế độ đã nhớ
    else if (!canSwitchMode && !nppMode && hasWorkModes(tenantType) && mode !== storedMode) setWorkMode(mode)
  }, [canSwitchMode, pathMode, storedMode, nppMode, tenantType, mode])

  // Màu theo chế độ cho đỡ nhầm: Quản lý = màu của cấp (Đại lý cam, CTV tím — cả nút, công tắc,
  // nền Glass), Bán hàng = xanh như Shop. Shop / Admin giữ nguyên màu của mình.
  const themeKey: RoleTheme = nppMode ? 'npp' : canSwitchMode && mode === 'sell' ? 'shop' : roleTheme

  // Đồng bộ lên thẻ html để portal, modal, tooltip thừa hưởng biến màu
  useEffect(() => {
    const root = document.documentElement
    root.setAttribute('data-role-theme', themeKey)
    if (canSwitchMode) root.setAttribute('data-work-mode', mode)
    else root.removeAttribute('data-work-mode')
    return () => {
      root.removeAttribute('data-role-theme')
      root.removeAttribute('data-work-mode')
    }
  }, [themeKey, canSwitchMode, mode])

  function switchMode(next: WorkMode) {
    markModeSwitch(next)
    setWorkMode(next, true)
    // Nhân viên: trang đầu tiên được vào ở chế độ mới (homeForMode là trang của chủ)
    nav(isStaff ? getHomeRouteForTenant(tenantType, role, staff, workModes) : homeForMode(tenantType, next))
  }

  const nppNav: NavItem[] = NPP_NAV.filter((i) => canWith(nppAdmin, i.anyOf)).map((i) => ({ to: i.to, label: i.label, ...(i.group ? { group: i.group } : {}) }))

  const currentNav: NavItem[] = nppMode
    ? nppNav
    : tenantType === 'super_admin' || tenantType === 'admin'
      ? ADMIN_NAV
      : (canSwitchMode || manageOnly) && mode === 'manage'
      ? isStaff ? usable(manageNav) : manageNav // chủ đơn vị: menu Quản lý như trước (không lọc)
      : usable(storeNav)

  // Điện thoại: tab, quạt Thêm, menu tài khoản theo app iOS (phone-nav.ts); khu NPP theo menu NPP
  const surface = phoneSurface(tenantType, mode)
  const nppTabs: PhoneTab[] = NPP_BOTTOM_PATHS.flatMap((to) => {
    const it = NPP_NAV.find((i) => i.to === to)
    return it && canWith(nppAdmin, it.anyOf) ? [{ to: it.to, label: NPP_BOTTOM_LABEL[it.to] ?? it.label, icon: NPP_PHONE_ICON[it.to] ?? 'admin', Icon: it.icon }] : []
  })
  const phoneTabsData = nppMode ? nppTabs : phoneTabs(surface, canAccess)
  // Mục "Sang Quản lý / Bán hàng" chỉ khi đổi chế độ được (nhân viên có trang ở cả hai bên)
  const phoneMoreData = nppMode ? [] : phoneMoreItems(surface, tenantType, canAccess).filter((i) => i.kind !== 'switch' || canSwitchMode)
  const phoneTabList: PhoneTab[] = nppMode ? nppTabs : phoneTabsData.map(withIcon)
  // Gom Đơn (Glass): ô "Gom Đơn" đứng trước ô đổi chế độ, chỉ khi menu bên đang có mục Gom Đơn (đã lọc enabled, chế độ, quyền)
  const phoneMoreBase: PhoneMoreEntry[] = phoneMoreData.map(withIcon)
  const gomdonInNav = !nppMode && currentNav.some((n) => n.to === '/inbox')
  const switchAt = phoneMoreBase.findIndex((i) => i.kind === 'switch')
  const phoneMore: PhoneMoreEntry[] = !gomdonInNav
    ? phoneMoreBase
    : switchAt < 0
      ? [...phoneMoreBase, GOMDON_FAN_ITEM]
      : [...phoneMoreBase.slice(0, switchAt), GOMDON_FAN_ITEM, ...phoneMoreBase.slice(switchAt)]
  const accountMenu: PhoneTab[] = nppMode
    ? NPP_ACCOUNT_MENU
    : accountMenuItems(tenantType, canHandleDownline(tenantType, role))
        .filter((i) => (isStaff ? staffCanUseLink(i.to, tenantType, staff, workModes) : canAccess(i.to.split('?')[0] ?? i.to)))
        .map(withIcon)
  const activePhone = nppMode ? nppActiveTab(location.pathname, nppTabs) : activePhoneTab(navMatchPath(location.pathname, [...phoneTabsData, ...phoneMore]), location.search, phoneTabsData, phoneMore)
  // Thanh tab Glass (30/10/2026): mọi ô nối thành một dải vuốt ngang + Cài đặt ghim cuối; Gom Đơn ngay sau 4 tab chính.
  // Khu NPP giữ 4 ô + "Thêm" mở cả menu NPP.
  const dock: PhoneMoreEntry[] = nppMode
    ? []
    : phoneDockItems(surface, tenantType, canAccess, gomdonInNav ? [GOMDON_FAN_ITEM] : [])
        .filter((i) => i.kind !== 'switch' || canSwitchMode)
        .map((i) => (i === GOMDON_FAN_ITEM ? GOMDON_FAN_ITEM : withIcon(i)))
  const settingsTab: PhoneTab = withIcon(PHONE_SETTINGS)
  const activeDock = nppMode ? null : activeDockTab(navMatchPath(location.pathname, dock), location.search, dock, ['/qr', ...accountMenu.map((i) => i.to)])
  const isAdminTenant = tenantType === 'super_admin' || tenantType === 'admin'
  const canCreateOrder = !nppMode && !isAdminTenant && hasAccess(role, isStaff ? '/orders/new' : '/orders', tenantType, staff, workModes)
  // Nút góc (docs/KE_HOACH_BANG_GIA_KHI_TAO_DON_VI.md §4.1): chế độ Quản lý / khu NPP → Thêm tài khoản (mở ngăn Thêm tài khoản
  // của Mạng lưới / Đơn vị), Bán hàng → Tạo đơn như cũ. Nhân viên cần quyền "Tạo cấp dưới" (NPP: quyền tạo đơn vị).
  const addUnitTo = nppMode
    ? canWith(nppAdmin, ['units.agency.manage', 'units.ctv.manage', 'units.shop.manage']) ? '/npp/units?them=1' : null
    : (canSwitchMode || manageOnly) && mode === 'manage' && (!isStaff || staff.permissions.includes('network.manage'))
      ? '/network?them=1'
      : null
  const primaryAction: 'addUnit' | 'createOrder' | null = addUnitTo ? 'addUnit' : canCreateOrder ? 'createOrder' : null
  const activePath = activeNavPath(navMatchPath(location.pathname, currentNav), currentNav)
  const currentLabel = currentNav.find((i) => i.to === activePath)?.label ?? ''

  // Đăng xuất hỏi đánh giá trước (docs/KE_HOACH_DANH_GIA_NEN_TANG.md) — không có bộ câu hỏi / lỗi thì thoát ngay
  async function onLogout() {
    await askLogoutSurvey({
      context: nppMode ? 'npp' : 'unit',
      device: viewport === 'mobile' ? 'mobile' : 'desktop',
      proceed: async () => {
        await logout()
        nav('/login', { replace: true })
      },
    })
  }

  // Khu Quản trị NPP: tên NPP (Sunrise hiện "Sunrise", không phải tên sản phẩm) + vai trò quản trị.
  // Lối đổi qua lại khi tài khoản vừa là admin NPP vừa là thành viên đơn vị.
  const nppLabel = nppAdmin.npp ? (nppAdmin.npp.isDefault ? nppAdmin.npp.name : nppAdmin.npp.displayName) : 'Nhà phân phối'
  const shellName = nppMode ? nppLabel : shopName
  const shellLevel = nppMode ? { ...levelInfo, levelTag: nppAdmin.isNppOwner ? 'Chủ NPP' : 'Quản trị NPP' } : levelInfo
  const crossLink = nppMode
    ? auth.shops.length > 0
      ? { to: getHomeAfterNpp(tenantType), label: 'Vào đơn vị của tôi' }
      : null
    : auth.nppAdmin
    ? { to: '/npp', label: 'Vào Quản trị NPP' }
    : null

  // Đổi đơn vị (tài khoản vừa là chủ shop riêng vừa là nhân viên nơi khác, hoặc nhân viên nhiều đơn vị)
  const units: Shop[] = nppMode ? [] : auth.shops
  async function switchUnit(shopId: string) {
    if (shopId === auth.activeShopId) return
    const back = location.pathname + location.search
    // Rời trang đang mở TRƯỚC khi đổi token: trang của đơn vị cũ còn trên màn sẽ tải lại số liệu bằng token của đơn
    // vị mới (nhân viên → 403). Trang chờ không gọi gì ngoài phần chung của khung.
    nav('/doi-don-vi', { replace: true })
    try {
      await selectShop(shopId)
      const next = getAuthState().shops.find((x) => x.shopId === shopId)
      nav(getHomeRouteForTenant(next?.tenantType, next?.role, next?.staff, next?.workModes), { replace: true })
    } catch (err) {
      toast.error((err as Error)?.message || 'Không chuyển được đơn vị')
      nav(back, { replace: true })
    }
  }

  return {
    auth, shop, role, tenantType, roleTheme: themeKey, levelInfo: shellLevel, shopName: shellName, nppName, currentNav, canCreateOrder,
    /** Ảnh đại diện của đơn vị đang mở; khu NPP không có */
    avatarUrl: nppMode ? null : shopSettings?.avatarUrl ?? null,
    primaryAction, addUnitTo,
    isStaff, staff, units, switchUnit,
    /** Nút Giới thiệu (mã QR to) ở thanh trên: chủ Shop — link ngang hàng; chủ CTV ở giao diện Bán hàng — link shop tự do
     *  (docs/KE_HOACH_SHOP_GIOI_THIEU.md §9). Quản lý giữ nút Thêm tài khoản. GET /settings/shop/referral chỉ chủ. */
    canPeerRefer: !nppMode && !isStaff && (tenantType === 'shop' || (tenantType === 'ctv' && mode === 'sell')),
    /** 'free' — CTV: hộp gọn (QR, link, Chia sẻ); 'peer' — Shop: thêm số đã giới thiệu, Sao chép, Tải mã QR */
    referKind: (tenantType === 'ctv' ? 'free' : 'peer') as 'free' | 'peer',
    referLabel: tenantType === 'ctv' ? 'Giới thiệu shop' : 'Giới thiệu ShopTrack',
    phoneTabs: phoneTabList, phoneMore, accountMenu, activePhone, dock, settingsTab, activeDock,
    nppMode, crossLink,
    /** Đầu menu tài khoản (điện thoại) mở trang này */
    accountHome: nppMode ? '/npp/account' : isStaff ? '/settings?tab=access' : '/settings?tab=general',
    activePath, currentLabel, onLogout, navBadgeFor,
    canSwitchMode, mode, modeLabel: WORK_MODE_LABEL[mode], modeChosen, switchMode,
  }
}

export type ShellModel = ReturnType<typeof useShellModel>

/**
 * Khung Glass (điện thoại) đưa dữ liệu khung xuống trang — trang Cài đặt dựng phần menu tài khoản từ đây. Không gọi
 * useShellModel lần hai trong trang: hook gắn data-role-theme lên <html> và gỡ khi trang rời đi.
 */
export const ShellContext = createContext<ShellModel | null>(null)
export const useShell = () => useContext(ShellContext)

/** Mục menu tài khoản (điện thoại) nhân viên dùng được — mục Cài đặt theo staffSettingsTabs, trang khác theo quyền. */
function staffCanUseLink(to: string, tenantType: string, staff: Shop['staff'], workModes: Shop['workModes']): boolean {
  const [path, query] = to.split('?')
  if (path === '/settings') {
    const tab = new URLSearchParams(query ?? '').get('tab')
    return !tab || staffSettingsTabs(staff?.permissions ?? []).includes(tab)
  }
  if (path === '/feedback' && query?.includes('downline')) return false
  return hasAccess('staff', path ?? to, tenantType, staff, workModes)
}

/** Mục Cài đặt nhân viên thấy: quyền của mình, bảo mật, thông báo; kho lấy hàng nếu được cấp. */
export function staffSettingsTabs(permissions: readonly string[]): string[] {
  return ['access', ...(permissions.includes('warehouses.manage') ? ['warehouse'] : []), 'security', 'notifications']
}

/** Trang đơn vị khi rời khu Quản trị NPP. */
function getHomeAfterNpp(tenantType: string): string {
  return tenantType === 'agency' ? '/agency/overview' : tenantType === 'ctv' ? '/ctv/dashboard' : '/app'
}

export type { RoleTheme }
